import { Component, input, output, viewChild } from '@angular/core';
import { RouterLink } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { Avatar } from 'primeng/avatar';
import { Button } from 'primeng/button';
import { Menu } from 'primeng/menu';
import { Tag } from 'primeng/tag';
import { Skeleton } from 'primeng/skeleton';
import { TableModule, TablePageEvent } from 'primeng/table';

import { CongregacionFiltro } from '../../enums/congregacion-filtro.enum';
import {
  buildMuelleMenuItems,
  buildPersonTableMenuItems,
  PersonTableMenuAction,
} from '../../helpers/build-person-table-menu';
import { capitalizeWords } from '../../helpers/nombre-texto.helper';
import { PersonTableColumn, PersonTableRow } from '../../interfaces/person-table.interface';
import { EdadPipe } from '../../pipes/edad.pipe';

export interface PersonTablePageChange {
  /** Pagina 1-based para el API. */
  page: number;
  rows: number;
}

export interface PersonTableActionEvent {
  action: PersonTableMenuAction;
  row: PersonTableRow;
}

@Component({
  selector: 'app-person-table',
  standalone: true,
  imports: [TableModule, Button, RouterLink, Avatar, Tag, EdadPipe, Menu, Skeleton],
  templateUrl: './person-table.component.html',
  styleUrl: './person-table.component.scss',
})
export class PersonTableComponent {
  readonly skeletonRows = [0, 1, 2, 3, 4, 5, 6, 7];
  readonly roleTagStyle = {
    backgroundColor: 'var(--p-primary-color)',
    color: 'var(--p-primary-contrast-color, #fff)',
    fontSize: '0.65rem',
    padding: '0.05rem 0.3rem',
    lineHeight: '1.1',
  };
  readonly rows = input<PersonTableRow[]>([]);
  readonly columns = input<PersonTableColumn[]>([]);
  readonly loading = input(false);
  readonly emptyMessage = input('No hay personas para mostrar.');

  /** Filtro activo de Congregacion (define items del menu). */
  readonly filtro = input<CongregacionFiltro | string>(CongregacionFiltro.Ninguno);
  /** Id del usuario en sesion (acciones Muelle). */
  readonly yoId = input<string | null>(null);
  /** Solo admin ve Reiniciar faltas en el menu. */
  readonly isAdmin = input(false);
  /** En muelle el menu no incluye chat, perfil ni editar. */
  readonly modo = input<'congregacion' | 'muelle'>('congregacion');
  /** El lider ve "Agregar a mi celula" en el menu del muelle. */
  readonly actorEsLider = input(false);

  /** Activa el paginador (off por defecto). */
  readonly paginator = input(false);
  /** Filas por pagina cuando paginator esta activo. */
  readonly rowsPerPage = input(12);
  /**
   * Paginacion server-side: el padre carga solo la pagina pedida.
   * Requiere totalRecords y escuchar pageChange.
   */
  readonly lazy = input(false);
  /** Total de registros en el servidor (solo lazy). */
  readonly totalRecords = input(0);
  /** Indice 0-based del primer registro de la pagina actual (lazy). */
  readonly first = input(0);

  readonly profileClick = output<string>();
  readonly pageChange = output<PersonTablePageChange>();
  readonly menuAction = output<PersonTableActionEvent>();

  readonly optionsMenu = viewChild<Menu>('optionsMenu');

  menuItems: MenuItem[] = [];

  trackById(_index: number, row: PersonTableRow): string {
    return row.id;
  }

  mostrarPalabras(value?: string | null): string {
    return capitalizeWords(value);
  }

  onProfile(id: string): void {
    this.profileClick.emit(id);
  }

  openOptions(event: Event, row: PersonTableRow): void {
    if (row.sinMenu) {
      return;
    }
    const onAction = (action: PersonTableMenuAction, actionRow: PersonTableRow): void => {
      this.menuAction.emit({ action, row: actionRow });
    };
    this.menuItems =
      this.modo() === 'muelle'
        ? buildMuelleMenuItems({
            row,
            yoId: this.yoId(),
            isLider: this.actorEsLider(),
            onAction,
          })
        : buildPersonTableMenuItems({
            filtro: this.filtro(),
            row,
            yoId: this.yoId(),
            isAdmin: this.isAdmin(),
            onAction,
          });
    this.optionsMenu()?.toggle(event);
  }

  onPage(event: TablePageEvent): void {
    if (!this.lazy()) {
      return;
    }
    const rows = event.rows || this.rowsPerPage();
    const page = Math.floor((event.first ?? 0) / rows) + 1;
    this.pageChange.emit({ page, rows });
  }
}
